import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase-client';

const listOptions = httpsCallable(functions, 'listCourseAssetOptions');
const optionCache = new Map();

function reservationId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `reservation_${crypto.randomUUID()}`
    : `reservation_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function loadOptions(teamId, courseType) {
  const key = `${teamId}:${courseType}`;
  if (!optionCache.has(key)) {
    const request = Promise.resolve(listOptions({ teamId, courseType }))
      .then((result) => result?.data || { fleets: [], members: [] })
      .catch((error) => {
        optionCache.delete(key);
        throw error;
      });
    optionCache.set(key, request);
  }
  return optionCache.get(key);
}

function eligibleMembers(members, boat) {
  const required = new Set(boat?.requiredTags || []);
  if (!required.size) return members;
  return members.filter((member) => (member.tags || []).some((tag) => required.has(tag)));
}

function sessionBounds(session = {}) {
  if (session.start && session.end) {
    const start = new Date(session.start);
    const end = new Date(session.end);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) return { start, end };
  }
  if (session.date && session.startTime && session.endTime) {
    const start = new Date(`${session.date}T${session.startTime}`);
    const end = new Date(`${session.date}T${session.endTime}`);
    if (end <= start) end.setDate(end.getDate() + 1);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) return { start, end };
  }
  return null;
}

function effectiveWindow(session, reservation) {
  const bounds = sessionBounds(session);
  if (!bounds) return '';
  const start = new Date(bounds.start.getTime() - Number(reservation.startsBeforeMinutes || 0) * 60000);
  const end = new Date(bounds.end.getTime() + Number(reservation.endsAfterMinutes || 0) * 60000);
  const format = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `${format.format(start)} – ${format.format(end)}`;
}

export default function CourseBoatReservations({ teamId, courseType, session, onChange, assetOptions = null }) {
  const [options, setOptions] = useState(assetOptions || { fleets: [], members: [] });
  const [loading, setLoading] = useState(!assetOptions);
  const [error, setError] = useState('');
  const reservations = useMemo(
    () => (Array.isArray(session?.assetReservations) ? session.assetReservations : []),
    [session?.assetReservations],
  );

  useEffect(() => {
    if (assetOptions) {
      setOptions(assetOptions);
      setError('');
      setLoading(false);
      return undefined;
    }
    let active = true;
    setError('');
    setLoading(true);
    loadOptions(teamId, courseType).then((result) => {
      if (!active) return;
      setOptions(result || { fleets: [], members: [] });
      setLoading(false);
    }).catch(() => {
      if (!active) return;
      setError('Could not load club boats. Try again.');
      setLoading(false);
    });
    return () => { active = false; };
  }, [assetOptions, courseType, teamId]);

  const boats = useMemo(() => {
    const eligible = options.fleets.flatMap((group) => (group.boats || []).map((boat) => ({ ...boat, fleet: group.fleet })));
    const known = new Set(eligible.map((boat) => `${boat.fleet}:${boat.id}`));
    const unavailableSelections = reservations
      .filter((row) => row.boatId && !known.has(`${row.fleet}:${row.boatId}`))
      .map((row) => ({ id: row.boatId, fleet: row.fleet, name: row.boatName || 'Unavailable boat', requiredTags: [], unavailable: true }));
    return [...eligible, ...unavailableSelections];
  }, [options, reservations]);
  const availableBoats = boats.filter((boat) => !reservations.some((row) => row.fleet === boat.fleet && row.boatId === boat.id));
  if (!loading && !error && boats.length === 0 && reservations.length === 0) return null;

  const patchReservation = (id, patch) => onChange(reservations.map((row) => row.id === id ? { ...row, ...patch } : row));
  const addReservation = () => {
    const boat = availableBoats[0];
    if (!boat) return;
    const members = eligibleMembers(options.members, boat);
    onChange([...reservations, {
      id: reservationId(), fleet: boat.fleet, boatId: boat.id,
      responsibleMemberId: members[0]?.id || '', startsBeforeMinutes: 0, endsAfterMinutes: 0,
    }]);
  };

  return <section className="course-boat-reservations" aria-label="Course boat reservations">
    <div className="course-boat-reservations__heading">
      <div><strong>Club boats</strong><p>Optional. Reservations block the normal boat-booking calendar as soon as this course is saved.</p></div>
      <button type="button" className="button button-secondary btn btn--secondary btn--xs" disabled={!availableBoats.length} onClick={addReservation}>Add boat</button>
    </div>
    {loading && <p className="text-muted">Loading eligible boats…</p>}
    {error && <p className="text-danger" role="alert">{error}</p>}
    <div className="course-boat-reservations__list">
      {reservations.map((reservation) => {
        const selectedBoat = boats.find((boat) => boat.fleet === reservation.fleet && boat.id === reservation.boatId);
        const members = eligibleMembers(options.members, selectedBoat);
        const responsibleEligible = members.some((member) => member.id === reservation.responsibleMemberId);
        return <div className="course-boat-reservation" key={reservation.id}>
          <label>Boat
            <select className="input" value={`${reservation.fleet}:${reservation.boatId}`} onChange={(event) => {
              const [fleet, boatId] = event.target.value.split(':');
              const boat = boats.find((row) => row.fleet === fleet && row.id === boatId);
              const nextMembers = eligibleMembers(options.members, boat);
              patchReservation(reservation.id, { fleet, boatId, responsibleMemberId: nextMembers.some((member) => member.id === reservation.responsibleMemberId) ? reservation.responsibleMemberId : (nextMembers[0]?.id || '') });
            }}>
              {boats.filter((boat) => (boat.fleet === reservation.fleet && boat.id === reservation.boatId) || (!boat.unavailable && !reservations.some((row) => row.fleet === boat.fleet && row.boatId === boat.id))).map((boat) => <option key={`${boat.fleet}:${boat.id}`} value={`${boat.fleet}:${boat.id}`}>{boat.name}{boat.unavailable ? ' · unavailable' : options.fleets.length > 1 ? ` · ${boat.fleet}` : ''}</option>)}
            </select>
          </label>
          <label>Responsible member
            <select className="input" value={responsibleEligible ? reservation.responsibleMemberId : ''} onChange={(event) => patchReservation(reservation.id, { responsibleMemberId: event.target.value })}>
              <option value="">Choose a qualified member</option>
              {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
            </select>
          </label>
          <label>Minutes before start<input className="input" type="number" min="0" max="1440" value={reservation.startsBeforeMinutes || 0} onChange={(event) => patchReservation(reservation.id, { startsBeforeMinutes: Number(event.target.value || 0) })} /></label>
          <label>Minutes after end<input className="input" type="number" min="0" max="1440" value={reservation.endsAfterMinutes || 0} onChange={(event) => patchReservation(reservation.id, { endsAfterMinutes: Number(event.target.value || 0) })} /></label>
          <p className="text-muted course-boat-reservation__window"><strong>Effective window:</strong> {effectiveWindow(session, reservation) || 'Add the session time first.'} Availability is checked when you save.</p>
          <a className="btn btn--secondary btn--xs" href={`/teams/${encodeURIComponent(teamId)}?boatCalendar=${encodeURIComponent(reservation.fleet)}`}>View booking calendar</a>
          <button type="button" className="button button-danger btn btn--secondary btn--xs" onClick={() => onChange(reservations.filter((row) => row.id !== reservation.id))}>Remove boat</button>
          {selectedBoat?.unavailable && <p className="course-boat-reservation__error" role="alert">This boat is unavailable or out of service. Choose a replacement.</p>}
          {!responsibleEligible && <p className="course-boat-reservation__error" role="alert">Choose an active member who has one of this boat’s required qualifications.</p>}
        </div>;
      })}
    </div>
  </section>;
}
